import { requestKarangoApi } from "../../../core/api/karangoTransport";

export const MESSAGE_UNREAD_REFRESH_EVENT = "karango:messages:refresh-unread";

function dispatchMessageUnreadRefresh() {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return;
  }

  if (typeof Event === "function") {
    window.dispatchEvent(new Event(MESSAGE_UNREAD_REFRESH_EVENT));
  }
}

export async function listMessageThreads({
  signal,
  limit = 50,
  cursor,
  includePageInfo = false,
} = {}) {
  const params = new URLSearchParams();
  params.set("limit", String(limit));
  if (cursor) {
    params.set("cursor", String(cursor));
  }

  const payload = await requestKarangoApi(`/v1/threads?${params.toString()}`, {
    requiresAuth: true,
    signal,
  });

  const hasTotalUnreadCount = Object.prototype.hasOwnProperty.call(
    payload || {},
    "totalUnreadCount"
  );
  const result = {
    items: Array.isArray(payload?.threads) ? payload.threads : [],
    nextCursor: payload?.nextCursor || null,
    totalUnreadCount: hasTotalUnreadCount
      ? Math.max(0, Number(payload?.totalUnreadCount) || 0)
      : null,
  };
  return includePageInfo ? result : result.items;
}

export async function listThreadMessages(threadId, { limit = 50, before, beforeId, signal } = {}) {
  if (!threadId) {
    return [];
  }

  const params = new URLSearchParams();
  params.set("limit", String(limit));

  if (before) {
    params.set("before", String(before));
  }

  if (beforeId) {
    params.set("beforeId", String(beforeId));
  }

  const payload = await requestKarangoApi(
    `/v1/threads/${encodeURIComponent(String(threadId))}/messages?${params.toString()}`,
    {
      requiresAuth: true,
      signal,
    }
  );

  return Array.isArray(payload?.messages) ? payload.messages : [];
}

export async function listRemoteMessageRequests({
  direction = "received",
  signal,
  limit = 50,
  cursor,
  includePageInfo = false,
} = {}) {
  const safeDirection = direction === "sent" ? "sent" : "received";
  const params = new URLSearchParams();
  params.set("direction", safeDirection);
  params.set("limit", String(limit));
  if (cursor) {
    params.set("cursor", String(cursor));
  }

  const payload = await requestKarangoApi(
    `/v1/message-requests?${params.toString()}`,
    {
      requiresAuth: true,
      signal,
    }
  );

  const result = {
    items: Array.isArray(payload?.requests) ? payload.requests : [],
    nextCursor: payload?.nextCursor || null,
  };
  return includePageInfo ? result : result.items;
}

export async function createRemoteMessageRequest(receiverId, content) {
  if (!receiverId) {
    throw new Error("receiverId requis.");
  }

  return requestKarangoApi("/v1/message-requests", {
    method: "POST",
    requiresAuth: true,
    body: {
      receiverId,
      content,
    },
  });
}

export async function sendRemoteDirectMessage(receiverId, content) {
  if (!receiverId) {
    throw new Error("receiverId requis.");
  }

  return requestKarangoApi("/v1/messages", {
    method: "POST",
    requiresAuth: true,
    body: {
      receiverId,
      content,
    },
  });
}

export async function sendRemoteThreadMessage(threadId, content) {
  if (!threadId) {
    throw new Error("threadId requis.");
  }

  return requestKarangoApi(`/v1/threads/${encodeURIComponent(String(threadId))}/messages`, {
    method: "POST",
    requiresAuth: true,
    body: { content },
  });
}

export async function deleteRemoteThread(threadId) {
  if (!threadId) throw new Error("threadId requis.");
  return requestKarangoApi(
    `/v1/threads/${encodeURIComponent(String(threadId))}`,
    { method: "DELETE", requiresAuth: true }
  );
}

export async function clearRemoteThreadMessages(threadId) {
  if (!threadId) {
    throw new Error("threadId requis.");
  }

  return requestKarangoApi(
    `/v1/threads/${encodeURIComponent(String(threadId))}/messages`,
    { method: "DELETE", requiresAuth: true }
  );
}

export async function markThreadRead(threadId) {
  if (!threadId) {
    throw new Error("threadId requis.");
  }

  const payload = await requestKarangoApi(
    `/v1/threads/${encodeURIComponent(String(threadId))}/read`,
    { method: "POST", requiresAuth: true }
  );
  dispatchMessageUnreadRefresh();
  return payload;
}

export async function acceptRemoteMessageRequest(messageRequestId) {
  if (!messageRequestId) {
    throw new Error("messageRequestId requis.");
  }

  return requestKarangoApi(
    `/v1/message-requests/${encodeURIComponent(String(messageRequestId))}/accept`,
    {
      method: "POST",
      requiresAuth: true,
    }
  );
}

export async function rejectRemoteMessageRequest(messageRequestId) {
  if (!messageRequestId) {
    throw new Error("messageRequestId requis.");
  }

  return requestKarangoApi(
    `/v1/message-requests/${encodeURIComponent(String(messageRequestId))}/reject`,
    {
      method: "POST",
      requiresAuth: true,
    }
  );
}

export async function cancelRemoteMessageRequest(messageRequestId) {
  if (!messageRequestId) {
    throw new Error("messageRequestId requis.");
  }

  return requestKarangoApi(
    `/v1/message-requests/${encodeURIComponent(String(messageRequestId))}/cancel`,
    {
      method: "POST",
      requiresAuth: true,
    }
  );
}

export async function reportRemoteMessageThread(
  threadId,
  { reason = "other", details = "Signalement depuis la messagerie.", signal } = {}
) {
  if (!threadId) {
    return null;
  }

  const payload = await requestKarangoApi("/v1/reports", {
    method: "POST",
    requiresAuth: true,
    signal,
    body: {
      targetType: "MESSAGE_THREAD",
      targetId: threadId,
      reason,
      details,
    },
  });

  return payload?.report || null;
}

export async function reportRemoteMessage(
  messageId,
  { reason = "other", details = "Signalement depuis la messagerie.", signal } = {}
) {
  if (!messageId) {
    return null;
  }

  const payload = await requestKarangoApi("/v1/reports", {
    method: "POST",
    requiresAuth: true,
    signal,
    body: {
      targetType: "MESSAGE",
      targetId: messageId,
      reason,
      details,
    },
  });

  return payload?.report || null;
}
